import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import './SortLabel.css';

export type SortOption = 'created' | 'uploaded';
export type ViewMode = 'all' | 'new' | 'unlinked';

export interface SortLabelProps {
  /** Current sort option */
  sortBy: SortOption;
  /** Sort change handler */
  onSortChange: (sort: SortOption) => void;
  /** Number of new documents (only shown in 'created' sort mode) */
  newDocsCount?: number;
  /** Current view mode */
  viewMode: ViewMode;
  /** View mode change handler */
  onViewModeChange: (mode: ViewMode) => void;
  /** Label for 'created' sort option */
  createdLabel?: string;
  /** Label for 'uploaded' sort option */
  uploadedLabel?: string;
  /** Label for new documents button */
  newDocsLabel?: string;
  /** Label for "N new received" indicator (use {{count}} for number) */
  newReceivedLabel?: string;
  /** Label for back to all button */
  backToAllLabel?: string;
  /** Prefix for dropdown options (e.g., "Ordered by") */
  orderedByPrefix?: string;
  /** Label for document date option in dropdown (e.g., "Document date (issued)") */
  documentDateOptionLabel?: string;
  /** Label for received date option in dropdown (e.g., "Received date (uploaded)") */
  receivedDateOptionLabel?: string;
  /** FR-017 #7a — processed documents with no twin link; the entry shows only when > 0 */
  unlinkedCount?: number;
  /** Label for the unlinked-documents entry (use {{count}} for number) */
  unlinkedLabel?: string;
  /** Optional class name */
  className?: string;
}

/**
 * Sort label component with dropdown and "New Documents" view mode support.
 *
 * States:
 * 1. Standard dropdown ("Sort by document date" / "Sort by received date")
 * 2. "New documents (N)" button when new docs exist (only in 'created' mode)
 * 3. "Back to all" button when viewing new docs only
 * 4. An "Unlinked (N)" entry beside the sort control when unlinked documents exist
 *    (FR-017 #7a); its view shows "Back to all" plus the entry as the view's name.
 */
export const SortLabel: React.FC<SortLabelProps> = ({
  sortBy,
  onSortChange,
  newDocsCount = 0,
  viewMode,
  onViewModeChange,
  createdLabel = 'Sort by document date',
  uploadedLabel = 'Sort by received date',
  newDocsLabel = 'New documents',
  newReceivedLabel = '{{count}} new received',
  backToAllLabel = 'All documents',
  orderedByPrefix = '',
  documentDateOptionLabel = 'Document date (issued)',
  receivedDateOptionLabel = 'Received date (uploaded)',
  unlinkedCount = 0,
  unlinkedLabel = 'Unlinked ({{count}})',
  className = '',
}) => {
  const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'created', label: createdLabel },
    { value: 'uploaded', label: uploadedLabel },
  ];

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const currentOption = SORT_OPTIONS.find((opt) => opt.value === sortBy) || SORT_OPTIONS[0];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue: SortOption) => {
    onSortChange(optionValue);
    setIsOpen(false);
    // Reset to 'all' view when changing sort
    if (viewMode !== 'all') {
      onViewModeChange('all');
    }
  };

  const handleNewDocsClick = () => {
    onViewModeChange('new');
  };

  const handleBackToAllClick = () => {
    onViewModeChange('all');
  };

  // Determine which UI to show based on state
  const showNewDocsIndicator = sortBy === 'created' && newDocsCount > 0 && viewMode === 'all';
  const showBackToTimeline = viewMode !== 'all';
  const showUnlinkedEntry = viewMode === 'all' && unlinkedCount > 0;
  const unlinkedText = unlinkedLabel.replace('{{count}}', String(unlinkedCount));
  const showSortDropdown = !showNewDocsIndicator && !showBackToTimeline;

  return (
    <div className={`sort-label ${className}`} ref={menuRef}>
      {/* Back to timeline link (when viewing new docs) */}
      {showBackToTimeline && (
        <button
          className="sort-label-back"
          onClick={handleBackToAllClick}
          aria-label={backToAllLabel}
        >
          <ArrowLeft className="sort-label-back-icon" />
          <span>{backToAllLabel}</span>
        </button>
      )}

      {/* The unlinked view names itself beside the way back */}
      {viewMode === 'unlinked' && <span className="sort-label-view-name">{unlinkedText}</span>}

      {/* FR-017 #7a — entry to the unlinked-documents view */}
      {showUnlinkedEntry && (
        <button className="sort-label-unlinked" onClick={() => onViewModeChange('unlinked')}>
          {unlinkedText}
        </button>
      )}

      {/* Subtle new documents indicator (replaces sort dropdown) */}
      {showNewDocsIndicator && (
        <button
          className="sort-label-new-indicator"
          onClick={handleNewDocsClick}
          aria-label={`${newDocsCount} ${newDocsLabel}`}
        >
          <span className="sort-label-new-indicator-dot" />
          <span className="sort-label-new-indicator-text">
            {newReceivedLabel.replace('{{count}}', String(newDocsCount))}
          </span>
        </button>
      )}

      {/* Standard Sort Dropdown */}
      {showSortDropdown && (
        <>
          <button
            className="sort-label-button"
            onClick={() => setIsOpen(!isOpen)}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            title={`Documents are sorted by ${sortBy === 'created' ? 'document date' : 'received date'}`}
          >
            <span>{currentOption.label}</span>
          </button>

          {isOpen && (
            <div className="sort-label-menu" role="listbox">
              {SORT_OPTIONS.map((option) => {
                const isActive = option.value === sortBy;
                return (
                  <button
                    key={option.value}
                    className={`sort-label-option ${isActive ? 'sort-label-option-active' : ''}`}
                    onClick={() => handleSelect(option.value)}
                    aria-selected={isActive}
                    role="option"
                  >
                    <Check
                      className={`sort-label-option-check ${isActive ? 'sort-label-option-check--visible' : 'sort-label-option-check--hidden'}`}
                      data-testid="sort-label-option-check"
                      aria-hidden="true"
                    />
                    <span className="sort-label-option-prefix">{orderedByPrefix}</span>
                    <span>{option.value === 'created' ? documentDateOptionLabel : receivedDateOptionLabel}</span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SortLabel;
